'use client'

import { useMemo } from 'react'
import Image from 'next/image'
import { Movie } from '@/types'
import { getOptimizedImageUrl } from '@/lib/utils'

interface MovieCardProps {
  movie: Movie
  onClick: () => void
  isFutureLoading?: boolean
}

export default function MovieCard({ movie, onClick, isFutureLoading }: MovieCardProps) {
  const posterUrl = getOptimizedImageUrl(movie.posterUrl) || '/placeholder-movie.jpg'

  const hasOnlyPastShowtimes = useMemo(() => {
    if (isFutureLoading) return false
    if (!movie.showtimes || movie.showtimes.length === 0) return false
    // A showtime is still bookable while its purchaseUrl is present — AMC
    // keeps the URL active for a window after the show starts. Only show
    // "No Showtimes Left" once every showtime has lost its purchaseUrl AND
    // its start time has passed (belt-and-suspenders fallback for when the
    // scheduled feed adds future shows that don't have a purchaseUrl yet).
    const now = new Date()
    return movie.showtimes.every(s => {
      const hasPurchaseUrl = Boolean(s.purchaseUrl?.trim())
      
      // Use exact AMC booking cutoff time if available, otherwise fall back to show start time
      const cutoffDate = s.sellUntilDateTimeUtc
        ? new Date(s.sellUntilDateTimeUtc)
        : s.showDateTimeUtc
        ? new Date(s.showDateTimeUtc)
        : new Date(s.showDateTimeLocal)

      return !hasPurchaseUrl || cutoffDate < now
    })
  }, [movie.showtimes, isFutureLoading])

  const startDateLabel = useMemo(() => {
    if (isFutureLoading) return ''
    if (!movie.isUpcoming) return ''
    if (!movie.showtimes || movie.showtimes.length === 0) return ''

    const now = new Date()
    const futureShowtimes = movie.showtimes.filter(s => {
      const showtimeDate = s.showDateTimeUtc ? new Date(s.showDateTimeUtc) : new Date(s.showDateTimeLocal)
      return showtimeDate >= now
    })

    if (futureShowtimes.length === 0) return ''

    const uniqueDates = new Set(
      futureShowtimes.map(s => {
        const d = s.showDateTimeUtc ? new Date(s.showDateTimeUtc) : new Date(s.showDateTimeLocal)
        return d.toDateString()
      })
    )
    const isSingleDay = uniqueDates.size === 1

    const sorted = [...futureShowtimes].sort((a, b) => {
      const dateA = a.showDateTimeUtc ? new Date(a.showDateTimeUtc) : new Date(a.showDateTimeLocal)
      const dateB = b.showDateTimeUtc ? new Date(b.showDateTimeUtc) : new Date(b.showDateTimeLocal)
      return dateA.getTime() - dateB.getTime()
    })

    const earliestDate = sorted[0].showDateTimeUtc ? new Date(sorted[0].showDateTimeUtc) : new Date(sorted[0].showDateTimeLocal)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const prefix = isSingleDay ? '' : 'Starts '
    const suffix = isSingleDay ? ' Only' : ''

    if (earliestDate.toDateString() === today.toDateString()) {
      return ''
    } else if (earliestDate.toDateString() === tomorrow.toDateString()) {
      return `${prefix}Tomorrow${suffix}`
    } else {
      const dateStr = earliestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      return `${prefix}${dateStr}${suffix}`
    }
  }, [movie.showtimes, isFutureLoading])

  return (
    <button
      onClick={onClick}
      className={`group cursor-pointer overflow-hidden sm:rounded-lg sm:border sm:border-slate-200/60 sm:shadow-md hover:sm:shadow-lg transition-all flex flex-col w-full h-full ${
        hasOnlyPastShowtimes ? 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0' : ''
      }`}
    >
      {startDateLabel && (
        <div className="w-full bg-slate-100 text-slate-600 border-b border-slate-200 text-[10px] font-bold py-1 px-2 text-center uppercase tracking-wider shrink-0">
          {startDateLabel}
        </div>
      )}
      <div className="relative w-full aspect-[2/3] bg-gray-200 overflow-hidden">
        <Image
          src={posterUrl}
          alt={movie.movieName}
          fill
          className="object-cover group-hover:scale-105 transition-transform"
        />
        {movie.availableForAList === false && (
          <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
            Not on A-List
          </div>
        )}
        {hasOnlyPastShowtimes && (
          <div className="absolute top-2 left-2 bg-slate-900/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded">
            No Showtimes Left
          </div>
        )}
      </div>
      <div className="p-2 bg-white flex-1 flex flex-col justify-center items-center">
        <p className="text-xs font-semibold line-clamp-2 text-center">{movie.movieName}</p>
      </div>
    </button>
  )
}
